import React, { useEffect, useState, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { LinkProfile, LinkButton } from "../models";
import { useLinkProfile } from "../api/useLinkProfile";
import PageMeta from "../components/PageMeta";
import { BASE_URL, PROMOTION_VIDEO_URL } from "../utils/constants";

const DymanicOverlay: React.FC = () => {
  const navigate = useNavigate();

  const { username, linkPath } = useParams<{
    username: string;
    linkPath: string;
  }>();

  const { fetchLinkProfile } = useLinkProfile();

  const [profile, setProfile] = useState<LinkProfile | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchProfile = () => {
      let url = `${username}`;
      if (linkPath) {
        url += `/${linkPath}`;
      }

      fetchLinkProfile(url)
        .then((profile) => {
          setProfile(profile);
        })
        .catch((err) => {
          console.error("Failed to load profile", err);
          setError("Profile not found");
          navigate("/"); // TODO: navigate to 404
        });
    };

    if (username && !profile) {
      fetchProfile();
    }
  }, [username, linkPath]);

  const [isMuted, setIsMuted] = useState(true);
  const [showSoundButton, setShowSoundButton] = useState(false);

  const defaultOrderButtonColor = "rgba(16, 185, 129, 0.5)";
  const defaultButtonStyles = {
    fontSize: "1.5rem",
    color: "#000000",
    backgroundColor: "rgba(16, 185, 129, 0.5)",
  };

  // Get buttons sorted by order
  const sortedButtons = useMemo(() => {
    if (!profile) return [];
    const fixedButton = {
      order: 0,
      url: profile?.guide_link || "",
      text: profile?.guide_text || "Order Now",
      background_color: profile?.background_color || defaultButtonStyles.backgroundColor,
      text_color: profile?.text_color || defaultButtonStyles.color,
    } as LinkButton;

    return [fixedButton, ...(profile.buttons || [])].sort((a, b) => a.order - b.order);
  }, [profile?.buttons, profile?.guide_link, profile?.guide_text, profile?.background_color, profile?.text_color]);

  // Get buttons based on layout
  const getButtonsForLayout = useMemo(() => {
    if (!profile) return { top: [], bottom: [] };
    const layout = profile?.button_layout ?? 0;
    const buttons = sortedButtons;

    switch (layout) {
      case 0: // Single button
        return { top: [buttons[0]], bottom: [] };
      case 1: // Two buttons side by side
        return { top: buttons.slice(0, 2), bottom: [] };
      case 2: // Two buttons stacked
        return { top: [buttons[0]], bottom: [buttons[1]] };
      case 3: // Two buttons on top, one below
        return { top: buttons.slice(0, 2), bottom: [buttons[2]] };
      case 4: // Two buttons on top, two below
        return { top: buttons.slice(0, 2), bottom: buttons.slice(2, 4) };
      default:
        return { top: [buttons[0]], bottom: [] };
    }
  }, [profile?.button_layout, sortedButtons]);

  const handleOrderClick = () => {
    window.location.href = profile?.guide_link ? profile?.guide_link : BASE_URL;
  };

  const handleAboutClick = () => {
    window.location.href = profile?.about_link ? profile?.about_link : BASE_URL;
  };

  const renderButton = (button: LinkButton, index: number, isTop: boolean, rowLength: number) => {
    if (!button) return null;

    const bgColor = button.background_color || defaultButtonStyles.backgroundColor;
    const textColor = button.text_color || defaultButtonStyles.color;
    const isTwoButtonsInRow = rowLength === 2;

    const brightenColor = (bg: string) => {
      let bright = bg;
      if (bg.startsWith("#")) {
        const hex = bg.replace("#", "");
        if (hex.length === 3 || hex.length === 6) {
          let num = parseInt(
            hex.length === 3
              ? hex
                  .split("")
                  .map((x) => x + x)
                  .join("")
              : hex,
            16
          );
          let r = Math.min(255, ((num >> 16) & 0xff) + 40);
          let g = Math.min(255, ((num >> 8) & 0xff) + 40);
          let b = Math.min(255, (num & 0xff) + 40);
          bright = `rgb(${r},${g},${b})`;
        }
      } else if (bg.startsWith("rgb")) {
        const nums = bg.match(/[\d.]+/g);
        if (nums) {
          let [r, g, b, a] = nums.map(Number);
          r = Math.min(255, r + 40);
          g = Math.min(255, g + 40);
          b = Math.min(255, b + 40);
          bright =
            a !== undefined
              ? `rgba(${r},${g},${b},${a})`
              : `rgb(${r},${g},${b})`;
        }
      }
      return bright;
    };

    return (
      <button
        key={`${button.order}-${index}`}
        className={`order-button-${button.order}`}
        onClick={() => {
          if (button.url) {
            window.location.href = button.url;
          }
        }}
        style={{
          width: isTwoButtonsInRow ? "48%" : "98%",
          height: "clamp(3rem, 8vh, 4rem)",
          backgroundColor: bgColor,
          color: textColor,
          fontSize: "clamp(1.25rem, 4vw, 1.875rem)",
          fontWeight: "bold",
          paddingLeft: "clamp(1rem, 3vw, 1.5rem)",
          paddingRight: "clamp(1rem, 3vw, 1.5rem)",
          paddingTop: "clamp(1rem, 3vw, 1.5rem)",
          paddingBottom: "clamp(1rem, 3vw, 1.5rem)",
          borderRadius: "9px",
          boxShadow:
            "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)",
          transition: "all 300ms",
          pointerEvents: "auto",
          border: "none",
          zIndex: 2,
          margin: isTwoButtonsInRow ? "0 1%" : "0",
          overflow: "hidden",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          wordWrap: "break-word",
          wordBreak: "break-word",
          hyphens: "auto",
        }}
        onMouseOver={(e) => {
          e.currentTarget.style.backgroundColor = brightenColor(bgColor);
        }}
        onMouseOut={(e) => {
          e.currentTarget.style.backgroundColor = bgColor;
        }}
      >
        <span
          style={{
            overflow: "hidden",
            wordWrap: "break-word",
            wordBreak: "break-word",
            maxWidth: "100%",
            display: "block",
            lineHeight: "1.2",
            textAlign: "center",
          }}
        >
          {button.text || "Button"}
        </span>
      </button>
    );
  };

  // 添加 useEffect 钩子来控制页面滚动
  useEffect(() => {
    // 保存原始的 overflow 样式
    const originalStyle = window.getComputedStyle(document.body).overflow;

    // 禁用滚动
    document.body.style.overflow = "hidden";

    // 组件卸载时恢复原始样式
    return () => {
      document.body.style.overflow = originalStyle;
    };
  }, []);

  useEffect(() => {
    // 添加动画的样式
    const style = document.createElement("style");
    style.innerHTML = `
      @keyframes pulse {
        0% { transform: scale(1); opacity: 1; }
        50% { transform: scale(1.1); opacity: 0.8; }
        100% { transform: scale(1); opacity: 1; }
      }
      
      @media (max-width: 768px) {
        .sound-button {
          width: 40px !important;
          height: 40px !important;
        }
        
        .sound-button svg {
          width: 24px !important;
          height: 24px !important;
        }
        
        .about-button {
          font-size: 12px !important;
          padding: 6px 10px !important;
        }
        
        .order-button,
        [class^="order-button-"] {
          font-size: 1.25rem !important;
          padding: 1rem 1.25rem !important;
        }
      }
      
      @media (min-width: 1200px) {
        .sound-button {
          width: 60px !important;
          height: 60px !important;
        }
        
        .sound-button svg {
          width: 48px !important;
          height: 48px !important;
        }
        
        .about-button {
          font-size: 16px !important;
          padding: 10px 14px !important;
        }
        
        .order-button,
        [class^="order-button-"] {
          font-size: 2.25rem !important;
          padding: 2rem 2.25rem !important;
        }
      }
    `;
    document.head.appendChild(style);

    // 清理函数
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  const videoURL = new URL(profile?.video_link ? profile?.video_link : PROMOTION_VIDEO_URL);
  videoURL.searchParams.set("autoplay", "1");
  videoURL.searchParams.set("loop", "1");
  videoURL.searchParams.set("rel", "0");
  videoURL.searchParams.set("native_context_menu", "0");

  videoURL.searchParams.set("controls", "0");
  videoURL.searchParams.set("progress_bar", "0");
  videoURL.searchParams.set("fullscreen_button", "0");
  videoURL.searchParams.set("timestamp", "0");
  videoURL.searchParams.set("play_button", "0");
  videoURL.searchParams.set("volume_control", "0");
  videoURL.searchParams.set("closed_caption", "0");

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
    if (event.origin !== "https://www.tiktok.com") {
      return;
    }

    const data = event.data;
    if (!data["x-tiktok-player"]) {
      return;
    }

    const iframe = document.querySelector("iframe");
    if (!iframe) {
      console.error("Tiktok iframe not found");
      return;
    }

    const contentWindow = iframe.contentWindow;
    if (!contentWindow) {
      console.error("Tiktok iframe content window not found");
      return;
    }

    // console.debug("tiktok message", event);

    switch (event.data.type) {
      case "onPlayerReady":
        setShowSoundButton(true);
        break;
      default:
        break;
    }
  };

  window.addEventListener("message", handleMessage);

  return () => {
    window.removeEventListener("message", handleMessage);
  };
}, []);

  const handleTouch = (e: React.MouseEvent) => {
    console.log("touchstart");
    const iframe = document.querySelector("iframe");
    if (!iframe) {
      console.error("Tiktok iframe not found");
      return;
    }

    const contentWindow = iframe.contentWindow;
    if (!contentWindow) {
      console.error("Tiktok iframe content window not found");
      return;
    }

    // 设置静音状态
    if (isMuted) {
      // 先播放，然后再取消静音
      contentWindow.postMessage({ type: "play", "x-tiktok-player": true }, "*");

      // 添加延迟，确保播放命令执行后再取消静音
      setTimeout(() => {
        contentWindow.postMessage(
          { type: "unMute", "x-tiktok-player": true },
          "*"
        );
        setIsMuted(false);
      }, 100);
    } else {
      contentWindow.postMessage({ type: "mute", "x-tiktok-player": true }, "*");
      setIsMuted(true);
    }

    // 获取点击事件的位置
    const clickX = e.clientX;
    const clickY = e.clientY;

    // 创建并分发一个模拟的点击事件到 iframe 的位置
    const iframeRect = iframe.getBoundingClientRect();
    const iframeX = clickX - iframeRect.left;
    const iframeY = clickY - iframeRect.top;

    // 防止事件冒泡，停止事件传播
    e.stopPropagation();

    // 手动分发事件到 iframe
    setTimeout(() => {
      // 尝试点击 iframe 的内容
      try {
        // 使用 postMessage 告诉 iframe 在特定位置模拟点击
        contentWindow.postMessage(
          {
            type: "simulateClick",
            x: iframeX,
            y: iframeY,
            "x-tiktok-player": true,
          },
          "*"
        );
      } catch (error) {
        console.error("无法向 iframe 发送点击事件:", error);
      }
    }, 10);
  };

  const pageTitle = profile?.title
    ? profile?.title
    : profile?.user_profile.name
    ? profile?.user_profile.name
    : "Vioo VIP";

  return (
    <div className="absolute h-screen w-full overflow-hidden bg-black">
      <PageMeta
        title={pageTitle}
        favicon={`${profile?.user_profile.profile_picture ?? "/favicon.ico"}`}
        description={profile?.user_profile.bio ?? "Powered by Vioo VIP"}
      />

      {/* 底层：视频页面 */}
      {profile && (
        <div className="absolute inset-0 z-0 bg-black">
          <iframe
            height="100%"
            width="100%"
            src={videoURL.toString()}
            allowFullScreen={false}
            title={profile?.title}
            style={{
              zIndex: 0,
              border: "none",
              pointerEvents: "auto",
            }}
            allow="autoplay; encrypted-media;"
          ></iframe>
          {/* <script async src="https://www.tiktok.com/embed.js"></script> */}
        </div>
      )}

      {/* 顶层：覆盖层 */}
      <div className="absolute inset-0 z-10 pointer-events-none">
        {/* Follow button below TikTok logo */}
            <button
            style={{
              position: "fixed",
              top: "3rem", // adjust as needed to be just below the TikTok logo
              right: "max(0.5rem, 1vw)",
              zIndex: 21,
              background: "none",
              border: "none",
              padding: 0,
              cursor: "pointer",
              pointerEvents: "inherit",
            }}
            aria-label="Follow"
            onClick={(e) => {
              // Pass click to background video iframe
              const iframe = document.querySelector("iframe");
              if (iframe) {
              const rect = iframe.getBoundingClientRect();
              // Simulate click at center of iframe
              const x = rect.left + rect.width / 2;
              const y = rect.top + rect.height / 2;
              const contentWindow = iframe.contentWindow;
              if (contentWindow) {
                contentWindow.postMessage(
                {
                  type: "simulateClick",
                  x: x - rect.left,
                  y: y - rect.top,
                  "x-tiktok-player": true,
                },
                "*"
                );
              }
              }
              // Optionally: prevent default button behavior
              e.preventDefault();
              e.stopPropagation();
            }}
            >
            <img
              src="/img/follow_me.gif"
              alt="Follow"
              style={{
              width: "100px",
              height: "auto",
              display: "block",
              filter: "drop-shadow(0 2px 8px rgba(0,0,0,0.25))",
              }}
            />
            </button>

        {/* 圆形头像 */}
        {/* <a href="/" style={{ textDecoration: 'none' }}>
          <div
            style={{
              position: 'absolute',
              opacity: '70%',
              top: '20rem',
              right: '1rem',
              width: '80px',
              height: '80px',
              borderRadius: '50%',
              overflow: 'hidden',
              border: '3px solid rgba(255, 255, 255, 0.8)',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
              pointerEvents: 'auto',
              zIndex: 20,
              cursor: 'pointer',
              transition: 'transform 0.2s ease-in-out'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'scale(1.1)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'scale(1)';
            }}
          >
            <img
              // src={profile?.user_profile.profile_picture || '/logo_512.png'}
              src={'/img/logo_512.png'}
              alt="Profile Avatar"
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover'
              }}
              onError={(e) => {
                // Fallback to default avatar if image fails to load
                e.currentTarget.src = '/img/logo_512.png';
              }}
            />
          </div>
        </a> */}

        {/* 添加声音按钮 */}
        <div
          hidden={!showSoundButton}
          style={{
            position: "fixed",
            top: "50%",
            transform: "translateY(-50%)",
            right: "max(0.5rem, 1vw)",
            pointerEvents: "auto",
            zIndex: 20,
          }}
        >
          <button
            hidden={!showSoundButton}
            onClick={handleTouch}
            className="sound-button"
            style={{
              width: "50px",
              height: "50px",
              backgroundColor: "rgba(0, 0, 0, 0.3)",
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              border: "2px solid rgba(255, 255, 255, 0.7)",
              cursor: "pointer",
              animation: isMuted ? "pulse 1.5s infinite" : "none",
              pointerEvents: "auto",
            }}
            aria-label={isMuted ? "Click to enable sound" : "Click to mute"}
          >
            {isMuted ? (
              <svg
                width="36"
                height="36"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M11 5L6 9H3V15H6L11 19V5Z"
                  fill="white"
                  stroke="white"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M23 9L17 15"
                  stroke="white"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M17 9L23 15"
                  stroke="white"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            ) : (
              <svg
                width="36"
                height="36"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M14.5 8.7C14.5 8.7 16 10.4 16 12C16 13.6 14.5 15.3 14.5 15.3"
                  stroke="white"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M17.5 6C17.5 6 20 8.8 20 12C20 15.2 17.5 18 17.5 18"
                  stroke="white"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M11 5L6 9H3V15H6L11 19V5Z"
                  fill="white"
                  stroke="white"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            )}
          </button>
          {isMuted && (
            <div
              style={{
                position: "absolute",
                top: "100%",
                right: 0,
                backgroundColor: "rgba(0, 0, 0, 0.7)",
                color: "white",
                padding: "4px 8px",
                borderRadius: "4px",
                fontSize: "clamp(12px, 1.5vw, 14px)",
                whiteSpace: "nowrap",
                marginTop: "4px",
                pointerEvents: "none",
              }}
            >
              Click to enable sound
            </div>
          )}

          {/* 添加"了解更多"按钮 */}
          {/* <button
            onClick={() => {
              handleAboutClick();
            }}
            className="about-button"
            style={{
              position: "absolute",
              top: "calc(100% + 40px)",
              right: 0,
              backgroundColor: "rgba(59, 130, 246, 0.4)",
              color: "white",
              padding: "8px 12px",
              borderRadius: "4px",
              fontSize: "clamp(12px, 1.5vw, 14px)",
              whiteSpace: "nowrap",
              border: "1px solid rgba(255, 255, 255, 0.5)",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              transition: "all 300ms",
              pointerEvents: "auto",
            }}
            onMouseOver={(e) =>
              (e.currentTarget.style.backgroundColor =
                "rgba(59, 130, 246, 0.9)")
            }
            onMouseOut={(e) =>
              (e.currentTarget.style.backgroundColor =
                "rgba(59, 130, 246, 0.4)")
            }
          >
            {profile?.about_text}
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              style={{ marginLeft: "4px" }}
            >
              <path
                d="M5 12H19"
                stroke="white"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M12 5L19 12L12 19"
                stroke="white"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button> */}
        </div>

        {/* Buttons overlay */}
        <div
          style={{
            position: "fixed",
            bottom: 0,
            paddingBottom: "max(2rem, 4vh)",
            paddingLeft: "max(2rem, 4vh)",
            paddingRight: "max(2rem, 4vh)",
            width: "100%",
            height: "auto",
            zIndex: 5,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "flex-end",
          }}
        >
          {/* Top row buttons */}
          {getButtonsForLayout.top.length > 0 && (
            <div
              style={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "center",
                width: "100%",
                gap: getButtonsForLayout.top.length === 2 ? "2%" : "0",
                marginBottom:
                  getButtonsForLayout.bottom.length > 0
                    ? "clamp(1rem, 2vh, 1.5rem)"
                    : "0",
              }}
            >
              {getButtonsForLayout.top.map((button, index) =>
                renderButton(button, index, true, getButtonsForLayout.top.length)
              )}
            </div>
          )}

          {/* Bottom row buttons */}
          {getButtonsForLayout.bottom.length > 0 && (
            <div
              style={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "center",
                width: "100%",
                gap: getButtonsForLayout.bottom.length === 2 ? "2%" : "0",
              }}
            >
              {getButtonsForLayout.bottom.map((button, index) =>
                renderButton(button, index, false, getButtonsForLayout.bottom.length)
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DymanicOverlay;